import React, { useContext, useEffect, useState } from 'react';

import { message } from 'components';
import { useParams } from 'react-router-dom';
import { AuthContext } from 'contexts';
import { useSearchParams } from 'hooks';
import {
  getFeedback,
  getFeedbackForCoop,
  getFeedbackSettings,
} from 'api/FeedbackController';
import getShowingData, { GetShowingDataDTO } from 'api/get/getShowingData';

import errorReporter from '@app/errorReporter';
import { InternalShowingDetailsPageProps } from './types';

import Loading from '../MyBusinessPage/components/Loading';
import ShowingDetailsPage from './ShowingDetailsPage';

const InternalShowingDetailsPage: React.FC<InternalShowingDetailsPageProps> = ({
  appointmentId: appointmentIdProp,
  tourFunctions,
  fromTourPage,
}) => {
  const { appointmentId: appointmentIdParam } = useParams<{
    appointmentId: string;
  }>();
  const appointmentId = appointmentIdParam || appointmentIdProp!;

  const queryParams = useSearchParams<{ isListingAgent: string }>();
  const isListingAgent = queryParams.isListingAgent === 'true';

  const [data, setData] = useState<GetShowingDataDTO | undefined>();

  const [loading, setLoading] = useState(true);

  const { auth, hasFeature } = useContext(AuthContext);

  const fetchData = async () => {
    setLoading(true);
    try {
      const body = await getShowingData(appointmentId, isListingAgent);
      setData(body);
      setLoading(false);
    } catch (err) {
      const msg = 'Error fetching appointment info';
      errorReporter.error(`${msg}: ${err}`);
      message.error(msg);
    }
  };

  const fetchFeedback = async () => {
    setLoading(true);
    try {
      const configFn = userType === 'coop' ? getFeedbackForCoop : getFeedback;
      let config = await configFn({
        listingId: appointment.listing.id,
        appointmentId: appointment.id,
      });

      if (userType === 'agent' && !config) {
        config = {
          template: (
            await getFeedbackSettings({
              listingId: appointment.listingId,
            })
          )?.template,
        };
      }
      setData(prev => ({
        ...prev!,
        feedbackConfig: config,
      }));
    } catch (err) {
      const msg = 'Error fetching feedback info';
      errorReporter.error(`${msg}: ${err}`);
      message.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleChange =
    <K extends keyof GetShowingDataDTO>(key: K) =>
    (update: GetShowingDataDTO[K]) =>
      setData(prev => ({
        ...prev!,
        [key]: update,
      }));

  useEffect(() => {
    fetchData();
  }, []);

  if (loading || !auth || !data) {
    return <Loading />;
  }

  const {
    appointment,
    listing,
    listingAppointmentConfig,
    userType,
    feedbackConfig,
    lockboxPhotoURL,
    offerDetails,
    tourDetails,
    covidSignatureType,
    permissions,
  } = data;
  return (
    <ShowingDetailsPage
      isListingAgent={isListingAgent}
      auth={{ ...auth, hasFeature }}
      userType={userType}
      appointment={appointment}
      setAppointment={handleChange('appointment')}
      fetchAppointment={fetchData}
      listing={listing}
      listingAppointmentConfig={listingAppointmentConfig}
      feedbackConfig={feedbackConfig}
      setFeedbackConfig={handleChange('feedbackConfig')}
      fetchFeedback={fetchFeedback}
      lockboxPhoto={lockboxPhotoURL ?? ''}
      offerDetails={offerDetails}
      tourDetails={tourDetails}
      signatureType={covidSignatureType}
      queryParams={queryParams}
      tourFunctions={tourFunctions}
      fromTourPage={fromTourPage}
      appointmentPermissions={permissions}
    />
  );
};

export default InternalShowingDetailsPage;
